import React, { useState, useEffect, useContext, useRef } from 'react'
import { trackPromise } from 'react-promise-tracker'

import {
    StyleSeries,
    SetEmptyYValuesToNull,
    StyleDrilldownDataPoints,
} from 'highcharts/HighchartsStyleHelper'
import {
    SetupDefaultOptions,
    FULL_HEIGHT_ONE_INPUT_ROW,
    DisableAllButISeries,
    DisableSeriesDataLabels,
} from 'highcharts/HighchartsHelper'
import { GlobalHighchartsStyleOptions } from 'highcharts/HighchartsOptions'
import { GetAssetCostComparison } from 'store/ChartApiClient'
import BreakdownStateRow from '../Components/BreakdownStateRow'
import LoadingWrapper from 'components/LoadingWrapper'
import HighchartsWrapper from '../../../../../../highcharts/HighchartsWrapper'
import { CurrencyFormatter } from '../../../../../../highcharts/HighchartsHelper'

const AssetValueCostComparisonStep = ({ id }) => {
    const PromiseArea = 'graph-asset-value-cost'

    const [breakdownStateValue, setBreakdownStateValue] = useState('Gross')
    const [coverageStateValue, setCoverageStateValue] = useState('None')
    const [durationStateValue, setDurationStateValue] = useState('Annual')
    const [showCatValue, setShowCatValue] = useState('No')

    const mainPlotBandId = 'main-plot-band'
    const mainPlotBand = {
        borderColor: '#0f0835',
        borderWidth: 4,
        color: '#0f0835', // Color value
        from: 1.4, // Start of the plot band
        to: 1.6, // End of the plot band
        zIndex: 2,
        id: mainPlotBandId,
    }

    const [chart, setChart] = useState({})

    const [options, setOptions] = useState({
        chart: {
            events: {
                drilldown: (e) => {
                    // var chart = this
                    var chart = e.target
                    var { seriesOptions } = e
                    if (seriesOptions && seriesOptions.id === 'Coverage Drilldown') {
                        setHideDuration(true)
                        setHideCat(true)

                        const {
                            options: { drilldown },
                        } = chart
                        //convert drilldown data into multi series graph
                        if (drilldown && drilldown.series && drilldown.series) {
                            chart.userOptions.titleChange = true
                            chart.userOptions.defaultTitle = chart.title.textStr

                            chart.xAxis[0].removePlotBand(mainPlotBandId)

                            const value = CurrencyFormatter.format(
                                chart.userOptions.aggregateData.incomeProtectionValue
                            )
                            chart.setTitle({
                                //styling
                                useHTML: true,
                                text: '<div>' + value + '</div>',
                                style: {
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    border: '1px solid white',
                                    borderRadius: '5px',
                                    color: '#fff',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    padding: '12px 28px',
                                },
                            })

                            let drillDownSeries = drilldown.series.filter(
                                (_) => _.id === 'Coverage Drilldown'
                            )
                            drillDownSeries = drillDownSeries.map((p) => StyleSeries(p))
                            if (drillDownSeries.length > 0) {
                                drillDownSeries.forEach((_) => {
                                    let seriesData = {
                                        ..._,
                                        id: _.name,
                                        dataLabels: { enabled: false },
                                    }
                                    chart.addSingleSeriesAsDrilldown(e.point, seriesData)
                                })

                                chart.applyDrilldown()
                            }
                        }
                    } else if (seriesOptions && seriesOptions.id === 'Assets') {
                        setHideDuration(true)
                        setHideCat(true)
                        chart.userOptions.titleChange = true
                        chart.userOptions.defaultTitle = chart.title.textStr

                        chart.xAxis[0].removePlotBand(mainPlotBandId)

                        const value = CurrencyFormatter.format(
                            chart.userOptions.aggregateData.totalAssetBenefit
                        )
                        chart.setTitle({
                            //styling
                            useHTML: true,
                            text: '<div>' + value + '</div>',
                            style: {
                                background: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid white',
                                borderRadius: '5px',
                                color: '#fff',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                padding: '12px 28px',
                            },
                        })
                    }
                },
                drillup: (e) => {
                    var chart = e.target
                    setHideDuration(false)
                    setHideCat(false)

                    if (chart && chart.userOptions.titleChange === true) {
                        chart.userOptions.titleChange = false
                        chart.xAxis[0].addPlotBand(mainPlotBand)
                        chart.setTitle({
                            //styling
                            useHTML: false,
                            text: chart.userOptions.defaultTitle,
                            style: GlobalHighchartsStyleOptions.title.style,
                        })
                    }
                },
            },
        },
    })
    const [error, setError] = useState(null)
    const [isLoaded, setLoaded] = useState(false)
    const [hideDuration, setHideDuration] = useState(false)
    const [hideCat, setHideCat] = useState(false)

    // want to scale the right out a bit to make it appear smaller. To do this
    // we are going to set the max of the second y-axis higher than the value
    // that highcharts would calculate.
    const GetYAxisMaxValue = (opts) => {
        const allDataPointsForPremiums = opts.series
            .filter((p) => p.yAxis === 1)
            .map((p) => p.data)
            .flat()

        const values = allDataPointsForPremiums.map((_) => _.y ?? 0)
        const maxPremiumValue = Math.max(...values)
        return maxPremiumValue * 1.75
    }

    useEffect(() => {
        const StyleGraph = (opts) => {
            opts = SetupDefaultOptions(opts, FULL_HEIGHT_ONE_INPUT_ROW, false)
            if (chart && chart.drilldownLevels && chart.drilldownLevels.length > 0) {
                //drill up if selection changes and drilled down
                setHideDuration(false)
                setHideCat(false)
                chart.drillUp()
            }

            if (opts.series) {
                opts.series = opts.series.map((p) => {
                    let series = StyleSeries(p)
                    return SetEmptyYValuesToNull(series)
                })
                const assetDrilldownSeriesIndex = opts.drilldown.series.findIndex(
                    (p) => p.id === 'Assets'
                )
                opts.drilldown.series[assetDrilldownSeriesIndex] = StyleDrilldownDataPoints(
                    opts.drilldown.series[assetDrilldownSeriesIndex],
                    1
                )
                DisableSeriesDataLabels(opts.drilldown.series)
                opts.drilldown = {
                    ...opts.drilldown,
                    activeAxisLabelStyle: {
                        textDecoration: 'none',
                        color: 'white',
                    },
                }
            }

            opts = {
                ...opts,
                xAxis: [
                    {
                        ...opts.xAxis,
                        plotBands: [mainPlotBand],
                    },
                ],
                yAxis: [
                    {
                        ...opts.yAxis,
                        title: {
                            text: 'Benefit Comparison',
                            style: {
                                fontSize: '20px',
                                fontWeight: '500',
                            },
                        },
                    },
                    {
                        ...opts.yAxis,
                        opposite: true,
                        max: GetYAxisMaxValue(opts),
                        title: {
                            text: 'Premium Comparison',
                            style: {
                                fontSize: '20px',
                                fontWeight: '500',
                            },
                        },
                    },
                ],
                plotOptions: {
                    ...opts.plotOptions,
                    column: {
                        ...opts.plotOptions.column,
                        stacking: 'normal',
                        dataLabels: {
                            ...opts.plotOptions.column.dataLabels,
                            enabled: true,
                        },
                    },
                    series: {
                        events: {
                            legendItemClick: (e) => {
                                const {
                                    target: { name, chart, visible },
                                } = e
                                //fixes issue with resetting selection after init
                                setOptions(chart.options)
                                // chart.reflow()
                            },
                        },
                    },
                },
            }

            if (!isLoaded) {
                DisableAllButISeries(opts.series, 3)
            }

            setLoaded(true)

            setOptions(opts)
        }

        trackPromise(
            GetAssetCostComparison(id, coverageStateValue, durationStateValue, showCatValue)
                .then((resp) => {
                    const { data } = resp
                    StyleGraph(data)
                })
                .catch((err) => {
                    setError(err)
                })
                .finally(() => {}),
            PromiseArea
        )
    }, [durationStateValue, coverageStateValue, showCatValue, id])

    if (error) {
        return <p>Something went wrong, try again later</p>
    }

    return (
        <LoadingWrapper area={PromiseArea}>
            <HighchartsWrapper
                options={options}
                chartCallback={(e) => {
                    setChart(e)
                }}
                printHeightPx={450}
            />
            <BreakdownStateRow
                hideBreakdown={true}
                removeNone={true}
                hideDuration={hideDuration}
                breakdownValue={breakdownStateValue}
                coverageValue={coverageStateValue}
                durationValue={durationStateValue}
                breakdownChange={setBreakdownStateValue}
                coverageChange={setCoverageStateValue}
                durationChange={setDurationStateValue}
                showCat={hideCat ? false : true}
                catChange={setShowCatValue}
                catValue={showCatValue}
            ></BreakdownStateRow>
        </LoadingWrapper>
    )
}

export default AssetValueCostComparisonStep
